<template>
  <v-data-table
    :headers="headers"
    class="has-expand elevation-1"
    :class="[selectedLeads.length ? 'selected' : '']"
    :items="leads"
    :loading="loading"
    :options.sync="options"
    :server-items-length="totalItems"
    :footer-props="{
      'items-per-page-options': [10, 20, 30, 40, 50],
      'show-first-last-page': true,
    }"
    show-select
    :disable-sort="!!selectedLeads.length"
    :sort-by="sortKey"
    :sort-desc="sortDesc"
    must-sort
    show-expand
    v-model="selectedLeads"
  >
    <template #top>
      <div class="table-header-wrapper">
          <div class="search-wrapper">
            <v-text-field
              :value="searchInput"
              @change="$emit('update:userSearchInput', $event)"
              placeholder="search users"
              hide-details
              :clearable="true"
              solo
            >
            <template #append>
              <button
                class="default-btn text minimal"
                @click="$emit('update:userSearchInput', searchInput)"
              >
                <v-icon>mdi-magnify</v-icon>
              </button>
            </template>
          </v-text-field>
          </div>

        <div class="filter-wrapper">
          <span class="help big">Filter:</span>

          <v-menu :close-on-content-click="true" offset-y max-height="250">
            <template v-slot:activator="{ on }">
              <v-btn class="default-btn link" v-on="on">
                {{ filterHotText }} <v-icon>mdi-chevron-down</v-icon>
              </v-btn>
            </template>

            <v-list>
              <v-list-item
                v-for="option in filterOptions.hot"
                :key="option.key"
                @click="updateFilter('hot', option.key)">
                {{ option.text || option.key }}
              </v-list-item>
            </v-list>
          </v-menu>

          <v-menu :close-on-content-click="true" offset-y max-height="250">
            <template v-slot:activator="{ on }">
              <v-btn class="default-btn link" v-on="on">
                {{ filterStatusText }} <v-icon>mdi-chevron-down</v-icon>
              </v-btn>
            </template>

            <v-list>
              <v-list-item
                v-for="option in filterOptions.status"
                :key="option.key"
                @click="updateFilter('status', option.key)">
                {{ option.text || option.key }}
              </v-list-item>
            </v-list>
          </v-menu>

          <v-menu :close-on-content-click="false" offset-y max-height="250">
            <template v-slot:activator="{ on }">
              <v-btn class="default-btn link" v-on="on">
                {{ filterProductText }} <v-icon>mdi-chevron-down</v-icon>
              </v-btn>
            </template>

            <v-list>
              <v-list-item
                v-for="option in filterOptions.product"
                :key="option.key"
                @click="updateFilter('product', option.key)">
                <v-simple-checkbox
                  :ripple="false"
                  class="mr-1"
                  :value="activeFilter.product[option.key]"
                  @input="updateFilter('product', option.key)"
                />
                {{ option.text || option.key }}
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>
    </template>

    <template v-if="!!selectedLeads.length" #header.status>
      {{ selectedLeads.length }} selected
    </template>

    <template v-if="!!selectedLeads.length" #header.deal.user.fullName>
      <button
        class="default-btn text small minimal"
        @click="$emit('promptStatus', selectedLeads.map(({ id }) => id))"
      >Assign Status</button>
    </template>

    <template v-if="!!selectedLeads.length" #header.deal.user.email>
      <button
        class="default-btn text small minimal"
        @click="$emit('remove', selectedLeads.map(({ id }) => id))"
      >Remove Selected</button>
    </template>

    <template #item.status="{ item, value }">
      <LeadStatusIndicator
        :status="value"
        @clicked="$emit('promptStatus', item.id)" />
    </template>

    <template #item.deal.user.fullName="{ item, value }">
      <div class="user-lead">
        <router-link
          v-if="value && displayType === 'admin'"
          class="default-btn link"
          :to="{ name: 'AdminUser', params: { id: item.deal.user.id }}"
          target="_blank"
        >{{ value }}</router-link>

        <span v-else>
          {{ value || item.deal.fullName }}
        </span>

        <span v-if="item.contactedAt" class="lead-hot">
          <v-icon>mdi-fire</v-icon> Hot Lead
        </span>
        <span v-if="item.featured" class="lead-featured">
          <v-icon>mdi-currency-usd</v-icon> Featured
        </span>
      </div>
    </template>

    <template #item.deal.user.email="{ item, value }">
      <a
        v-if="value"
        class="default-btn link"
        :href="'mailto:' + value"
      >{{ value }}</a>

      <a
        v-else-if="item.deal && item.deal.source === 'calculator'"
        class="default-btn link"
        :href="'mailto:' + value"
      >{{ item.deal.email }}</a>
    </template>

    <template #item.deal.user.phone="{ item, value }">
      <a
        v-if="value"
        class="default-btn link ma-0 pa-0"
        :href="'tel:' + value"
      >{{ value | phone }}</a>

      <a
        v-else-if="item.deal && item.deal.source === 'calculator'"
        class="default-btn link ma-0 pa-0"
        :href="'tel:' + item.deal.phone"
      >{{ item.deal.phone | phone }}</a>

      <template v-if="value && item.deal.user.extension">
        x{{ item.deal.user.extension }}
      </template>

      <template v-else-if="item.deal && item.deal.source === 'calculator' && item.deal.extension">
        x{{ item.deal.extension }}
      </template>
    </template>

    <template #item.deal.dealSize="{ value }">
      {{ value | currency }}
    </template>

    <template #item.productId="{ item, value }">
      <router-link
        class="default-btn link small"
        :to="{ name: 'OrganizationProductsEdit', params: {
          organizationId: item.product.organizationId,
          productId: item.productId,
        }}"
        target="_blank"
      >{{ item.product.internalName || item.product.title || value }}</router-link>
    </template>

    <template #item.deal.source="{ value }">
      <span v-if="value === 'questionnaire'">Lender Match</span>
      <span v-if="value === 'calculator'">Amortization Calculator</span>
    </template>

    <template #item.createdAt="{ value }">
      {{ value | date }}
    </template>

    <template #item.updatedAt="{ value }">
      {{ value | date }}
    </template>

    <template #item.actions="{ item }">
      <div class="button-group">
        <button
          class="default-btn text text-red minimal"
          @click="$emit('remove', [item.id])"
        ><v-icon color="inherit">mdi-delete</v-icon></button>
      </div>

    </template>

    <template #expanded-item="{ headers, item }">
      <td :colspan="headers.length">
        <div class="expanded-table-row-content">
          <h3>Match Details</h3>

          <dl class="grid-wrapper" v-if="item.deal">
            <template v-for="displayItem in dealItemsToDisplay">
              <dt :key="'title-' + displayItem.key">
                {{ displayItem.key | friendlyDisplayKeys }}
              </dt>
              <dd :key="'value-' + displayItem.key">
                {{ item.deal[displayItem.key] | friendlyDisplayValue(displayItem.key) }}
              </dd>
            </template>
          </dl>

          <div v-else>
            The deal for this lead was deleted.
          </div>
        </div>
      </td>
    </template>
  </v-data-table>
</template>

<script>
import LeadStatusIndicator from '@/modules/shared/components/LeadStatusIndicator'

export default {
  name: 'LeadsTable',
  components: {
    LeadStatusIndicator,
  },
  props: {
    activeFilter: {
      type: Object,
      default: () => ({}),
    },
    filterOptions: {
      type: Object,
      default: () => ({}),
    },
    leads: {
      type: Array,
      default: () => [],
    },
    loading: Boolean,
    totalItems: Number,
    sortKey: String,
    sortDesc: Boolean,
    userSearchInput: {
      type: String,
      default: '',
    },
    displayType: {
      type: String,
      default: 'organization',
    },

    headers: {
      type: Array,
      default: () => [
        {
          text: 'Status',
          value: 'status',
          class: 'interact',
        },
        {
          text: 'Full Name',
          value: 'deal.user.fullName',
          class: 'interact',
        },
        { text: 'Email', value: 'deal.user.email' },
        { text: 'Product', value: 'productId' },
        { text: 'Source', value: 'deal.source' },
        { text: 'Date Created', value: 'createdAt' },
        { text: 'Last Updated', value: 'updatedAt' },
      ],
    },
  },

  data: () => ({
    selectedLeads: [],
    options: {},
    dealItemsToDisplay: [
      { key: 'dealType' },
      { key: 'practiceType' },
      { key: 'dealSize' },
      { key: 'creditScore' },
      { key: 'loanPaymentTerms' },
      { key: 'loanType' },
      { key: 'lenderFeatures' },
      { key: 'hasDisclosureEvents' },
      { key: 'hasRegulatoryIssues' },
      { key: 'speedOfClosing' },
    ],
    searchInput: '',
  }),

  computed: {
    filterHotText () {
      const active = this.filterOptions.hot.find(({ key }) => key === this.activeFilter.hot)
      return (active || {}).text || (active || {}).key
    },

    filterProductText () {
      const activeProducts = Object.entries(this.activeFilter.product)
        .filter(([_, include]) => include)

      if (activeProducts.length === 0) {
        return 'No product selected'
      } else if (activeProducts.length === 1) {
        return this.filterOptions.product.find(({ key }) => key === activeProducts[0][0]).text || ''
      }

      return `${activeProducts.length} Products Selected`
    },

    filterStatusText () {
      const active = this.filterOptions.status.find(({ key }) => key === this.activeFilter.status)
      return (active || {}).text || (active || {}).key
    },
  },

  watch: {
    options (newVal, oldVal) {
      this.$emit('update:itemsPerPage', this.options.itemsPerPage)
      this.$emit('update:sortKey', this.options.sortBy[0])
      this.$emit('update:sortDesc', this.options.sortDesc[0])
      this.$emit('page:changed', this.options.page)
    },

    userSearchInput () {
      this.searchInput = this.userSearchInput
    },

    leads: {
      handler () { this.selectedLeads = [] },
      deep: true,
    },
  },

  methods: {
    updateFilter (filter, value) {
      if (filter === 'product') {
        if (value === 'all') {
          this.activeFilter.product = { all: true }
        } else {
          this.activeFilter.product = {
            ...this.activeFilter.product,
            [value]: !this.activeFilter.product[value],
            all: false,
          }
        }

        const selectedCount = Object.entries(this.activeFilter.product)
          .filter(([_, included]) => included).length

        if (selectedCount === 0 || selectedCount === this.filterOptions.product.length - 1) {
          this.activeFilter.product = { all: true }
        }
      } else {
        this.activeFilter[filter] = value
      }
      this.$emit('updatedFilter')
    },
  },
}
</script>

<style lang="scss" scoped>
.table-header-wrapper {
  margin-bottom: 10px !important;

  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;

  .filter-wrapper {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  .default-btn {
    span { margin-right: 5px; }
  }
}

td .default-btn.link {
  display: inline-block !important;
  min-height: 0;
  text-align: left;

  font-family: "Avenir Medium", sans-serif;
  font-size: 14px;
  line-height: 16px;
  letter-spacing: 0.3px;

}

.user-lead {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  white-space: nowrap;
  letter-spacing: 0.3px;

  .v-icon { font-size: 18px; }

  .lead-hot, .lead-featured {
    margin-left: 5px;
    display: flex;
    align-items: center;
    letter-spacing: 0.1px;
  }

  .lead-hot {
    color: #D37D3E;
    font-size: 10px;

    .v-icon {
      color: #D37D3E;
    }
  }

  .lead-featured {
    color: #38BE56;
    font-size: 10px;

    .v-icon {
      color: #38BE56;
    }
  }
}

.expanded-table-row-content .grid-wrapper {
  display: grid;
  grid-template-columns: auto 1fr;
  grid-gap: 5px 20px;
}

.v-data-table ::v-deep {
  .v-data-table__wrapper table tbody tr {
    td, th {
      padding: 0 8px;
    }
  }
}
</style>
